import * as React from "react";
import { useEffect, useRef } from "react";
import {
  useRive,
  useStateMachineInput,
  Layout,
  Fit,
  Alignment,
  type StateMachineInput,
  type Rive,
} from "@rive-app/react-canvas";

export interface CloudSdkRiveInputs {
  rive: Rive;
  getInput: (name: string) => StateMachineInput | null;
  listInputs: () => string[];
}

interface CloudSdkRiveProps {
  className?: string;
  stateMachine?: string;
  artboard?: string;
  fit?: Fit;
  alignment?: Alignment;
  scale?: number;
  onLoad?: (inputs: CloudSdkRiveInputs) => void;
}

export function CloudSdkRive({
  className,
  stateMachine = "State Machine 1",
  artboard,
  fit = Fit.Contain,
  alignment = Alignment.Center,
  scale = 1,
  onLoad,
}: CloudSdkRiveProps) {
  const hasCalledOnLoad = useRef(false);

  const { rive, RiveComponent } = useRive({
    src: "/rive/cloudsdk.riv",
    stateMachines: stateMachine,
    artboard,
    autoplay: true,
    layout: new Layout({ fit, alignment }),
  });

  useEffect(() => {
    if (rive && onLoad && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      onLoad({
        rive,
        getInput: (name: string) => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.find((input) => input.name === name) ?? null;
        },
        listInputs: () => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.map((input) => input.name) ?? [];
        },
      });
    }
  }, [rive, onLoad, stateMachine]);

  return (
    <div
      className={className}
      style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}
    >
      <RiveComponent />
    </div>
  );
}

export { useStateMachineInput };
